import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

import type { RoleName } from '@/stores/auth'
import { STAFF_ROLES, useAuthStore } from '@/stores/auth'
import AdminUsersView from '@/views/AdminUsersView.vue'
import AlertsView from '@/views/AlertsView.vue'
import ApiKeysView from '@/views/ApiKeysView.vue'
import AppShell from '@/views/AppShell.vue'
import AuditView from '@/views/AuditView.vue'
import CustomersView from '@/views/CustomersView.vue'
import DeploymentsView from '@/views/DeploymentsView.vue'
import HostDetailView from '@/views/HostDetailView.vue'
import HostsView from '@/views/HostsView.vue'
import InviteCodesView from '@/views/InviteCodesView.vue'
import JobDetailView from '@/views/JobDetailView.vue'
import JobsView from '@/views/JobsView.vue'
import LicensesView from '@/views/LicensesView.vue'
import LoginView from '@/views/LoginView.vue'
import AgentConfigTemplatesView from '@/views/AgentConfigTemplatesView.vue'
import AgentKindsView from '@/views/AgentKindsView.vue'
import MetricDefsView from '@/views/MetricDefsView.vue'
import PlatformsView from '@/views/PlatformsView.vue'
import PluginDetailView from '@/views/PluginDetailView.vue'
import PluginGrantsView from '@/views/PluginGrantsView.vue'
import PluginPublishersView from '@/views/PluginPublishersView.vue'
import PluginReviewsView from '@/views/PluginReviewsView.vue'
import PluginsView from '@/views/PluginsView.vue'
import ProductsView from '@/views/ProductsView.vue'
import RedeemCampaignDetailView from '@/views/RedeemCampaignDetailView.vue'
import RedeemCampaignsView from '@/views/RedeemCampaignsView.vue'
import RedeemReportsView from '@/views/RedeemReportsView.vue'
import ProvisionView from '@/views/ProvisionView.vue'
import RebindView from '@/views/RebindView.vue'
import RequestsView from '@/views/RequestsView.vue'
import ResellersView from '@/views/ResellersView.vue'
import SiteDetailView from '@/views/SiteDetailView.vue'
import SitesView from '@/views/SitesView.vue'
import ResellerCustomersView from '@/views/reseller/ResellerCustomersView.vue'
import ResellerLicensesView from '@/views/reseller/ResellerLicensesView.vue'
import ResellerRequestsView from '@/views/reseller/ResellerRequestsView.vue'

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * 可進入本路由的角色**精確清單**（批93-C 起唯一機制，取代原本的 `minRole`）。
     *
     * ★★ 為什麼不能再用「最低角色」：`reseller` 不在線性階序裡（rank 0），任何
     * `rank >= X` 的寫法對它都是 fail-closed 沒錯，但反過來「代理商可進、admin 不可進」
     * 的頁面（`/reseller/*`）用階序根本表達不出來。全部收斂成集合之後，
     * `router/index.spec.ts` 才有辦法機械檢查「每條路由都宣告過誰能進」。
     *
     * 缺席＝退回「我方員工才可進」（`isStaff`），但**不要**依賴這個預設：
     * spec 測試會要求每一條具名路由都顯式寫出來。
     */
    roles?: RoleName[]
    /**
     * 批99-A：這條路由屬於**代管中台**（`auth.hostingVisible` 為 false 時進不去）。
     *
     * 與 `roles` 正交——它問的不是「你是誰」而是「我們有沒有開放代管給你經手的產品」，
     * 兩道閘都要過。缺席＝與代管無關（一般頁面）。
     *
     * ★ 「部署」（`/deployments`）**刻意不掛**：那是授權部署監控（已依範圖過濾），
     *   掛上去會讓沒有代管產品的 operator 登入後整個側欄空手（裁示 A.0-A）。
     */
    hosting?: boolean
  }
}

/** 我方員工四級都能進（代管維運那一群）。 */
const STAFF: RoleName[] = [...STAFF_ROLES]
/** 授權相關：admin 以上。 */
const ADMIN: RoleName[] = ['admin', 'manager', 'superadmin']
/**
 * 營運主管以上（批110）：代理商公司、帳號管理、稽核紀錄。
 * ★ 帳號管理頁 manager **進得去但只改得動代理商窗口**——我方員工那幾列的按鈕在頁內用
 *   `auth.isSuperadmin` 反灰，後端 `_gate_admin_user_target` 才是真閘門。
 */
const MANAGER: RoleName[] = ['manager', 'superadmin']
/**
 * 超級管理者**專屬**（批147：API 金鑰）。後端那幾支是 `require_superadmin`，
 * 營運主管以下進來只會整頁 403——所以連入口都不給。
 */
const SUPERADMIN: RoleName[] = ['superadmin']
const RESELLER: RoleName[] = ['reseller']

export const routes: RouteRecordRaw[] = [
  { path: '/login', name: 'login', component: LoginView },
  {
    path: '/',
    component: AppShell,
    children: [
      // 進來一律先彈到自己的落地頁（見下方 landing()）——這條 redirect 沒有 name，
      // 不受 guard 的 meta.roles 檢查（目標路由才會被檢查）。
      { path: '', redirect: { name: 'requests' } },
      { path: 'requests', name: 'requests', component: RequestsView, meta: { roles: ADMIN } },
      { path: 'customers', name: 'customers', component: CustomersView, meta: { roles: ADMIN } },
      { path: 'licenses', name: 'licenses', component: LicensesView, meta: { roles: ADMIN } },
      // 部署＝唯讀監控，operator 可看（裁示 #7）
      {
        path: 'deployments',
        name: 'deployments',
        component: DeploymentsView,
        meta: { roles: STAFF },
      },
      { path: 'rebind', name: 'rebind', component: RebindView, meta: { roles: ADMIN } },
      // 邀請碼（批81 需求 11）：與客戶／授權同級＝admin+（後端也是 require_admin）。
      {
        path: 'invite-codes',
        name: 'invite-codes',
        component: InviteCodesView,
        meta: { roles: ADMIN },
      },
      // 兌換碼（批104）：與邀請碼同級＝admin+（後端 `require_admin` 起跳；建立與修改
      // 是 superadmin，那道閘在頁內按 `auth.isSuperadmin` 藏按鈕，後端 gate 才是真防線）。
      { path: 'redeem-campaigns', name: 'redeem-campaigns', component: RedeemCampaignsView, meta: { roles: ADMIN } },
      {
        path: 'redeem-campaigns/:id',
        name: 'redeem-campaign-detail',
        component: RedeemCampaignDetailView,
        meta: { roles: ADMIN },
      },
      // 兌換報表（批107 項5）：領用（發碼）與使用（兌換）兩種語意。與兌換碼同級＝admin+
      // （後端 `require_admin`）。★ 元件**與代理商那條共用**，差別只在 API 前綴與
      // 「代理商排行伺服器端算不算」——見 `RedeemReportsView.vue` 檔頭。
      {
        path: 'redeem-reports',
        name: 'redeem-reports',
        component: RedeemReportsView,
        meta: { roles: ADMIN },
      },
      // plugin 市集（批153 批A）：admin 以上（後端 `/api/admin/plugin-market/*` 讀與日常操作是
      // `require_admin`；發行者、公鑰、上下架、停權要營運主管，那幾顆按鈕在頁內按
      // `lib/pluginMarket.ts::canManageMarket` 收起來，後端 gate 才是真防線）。
      // ★ **不掛 `meta.hosting`**：市集與代管能力閘無關。代理商一律進不來（後端 rank 閘）。
      { path: 'plugins', name: 'plugins', component: PluginsView, meta: { roles: ADMIN } },
      { path: 'plugins/:id', name: 'plugin-detail', component: PluginDetailView, meta: { roles: ADMIN } },
      { path: 'plugin-grants', name: 'plugin-grants', component: PluginGrantsView, meta: { roles: ADMIN } },
      {
        path: 'plugin-publishers',
        name: 'plugin-publishers',
        component: PluginPublishersView,
        meta: { roles: ADMIN },
      },
      // 批153 批B：審核佇列（上架申請＋第三方公開 plugin 的待審版本）。admin 可看、可下載包
      // （後端 #26／#27 是 `require_admin`）；核准與婉拒是營運主管，按鈕在頁內收起來。
      { path: 'plugin-reviews', name: 'plugin-reviews', component: PluginReviewsView, meta: { roles: ADMIN } },
      // 代管中台：清單／詳情／告警都是 operator 的主戰場
      // ★ 這六條都掛 `meta.hosting`（批99-A）——後端對應的端點整個 router 有
      //   `require_hosting_scope`，沒開放代管產品的帳號進來只會整頁 403。
      { path: 'hosts', name: 'hosts', component: HostsView, meta: { roles: STAFF, hosting: true } },
      {
        path: 'hosts/:id',
        name: 'host-detail',
        component: HostDetailView,
        meta: { roles: STAFF, hosting: true },
      },
      // 批151 C7：〈全部站台〉（跨主機清單＋營運主管的機密反查）與〈站台詳情〉。operator 起可見
      // ——後端 `GET /sites`、`GET /sites/{id}` 在 operator 白名單；詳情裡授權／子證／站台機密
      // 三塊由後端依角色收掉。★ **只有我方員工**：代理商 realm 沒有這兩支端點（不加 reseller 版路由）。
      { path: 'sites', name: 'sites', component: SitesView, meta: { roles: STAFF, hosting: true } },
      {
        path: 'hosts/:id/sites/:siteId',
        name: 'site-detail',
        component: SiteDetailView,
        meta: { roles: STAFF, hosting: true },
      },
      { path: 'alerts', name: 'alerts', component: AlertsView, meta: { roles: STAFF, hosting: true } },
      // 派工：operator 看得到（升級／備份／診斷是他的工作），取消／重試的權限在頁內細分。
      { path: 'jobs', name: 'jobs', component: JobsView, meta: { roles: STAFF, hosting: true } },
      {
        path: 'jobs/:jobId',
        name: 'job-detail',
        component: JobDetailView,
        meta: { roles: STAFF, hosting: true },
      },
      // 開站精靈＝admin 以上（後端 preflight／provision 都是 require_admin）；不放 nav。
      {
        path: 'provision',
        name: 'provision',
        component: ProvisionView,
        meta: { roles: ADMIN, hosting: true },
      },
      // 產品（批93）：憑證設計與套裝。**admin 唯讀可見**——`RequestsView`／`LicensesView`
      // 要靠憑證設計把模組鍵翻成中文，admin 得看得到那份設計；寫操作在頁內按
      // `auth.isSuperadmin` 隱藏（後端 gate 才是真防線）。
      { path: 'products', name: 'products', component: ProductsView, meta: { roles: ADMIN } },
      // 批112：站台指標的顯示定義。**manager 起跳**——後端寫端點是 `require_manager`，
      // 整頁都是寫操作（讀端點雖然放寬到 staff，那是給部署頁翻譯用的）。
      {
        path: 'metric-defs',
        name: 'metric-defs',
        component: MetricDefsView,
        meta: { roles: MANAGER },
      },
      // 批112／1.0.44：主機設定的**欄位宣告**（設定樣板）。與「指標定義」同一族——
      // 都是我方在中台維護、出廠空表的資料，差別只在流向（樣板往外下發、指標往內收）。
      // **manager 起跳**：後端寫端點是 `require_manager`，整頁都是寫操作。
      {
        path: 'agent-config-templates',
        name: 'agent-config-templates',
        component: AgentConfigTemplatesView,
        meta: { roles: MANAGER },
      },
      // 批141（裁示 13）：agent 種類登錄。**admin 可看、manager 以上可改**——後端讀端點
      // `require_admin`（註冊主機、看主機詳情的人要看得懂「這台是哪一種 agent」）、寫端點
      // `require_manager`；寫入按鈕在頁內按 `auth.isManagerOrAbove` 收起來。
      // ★ 刻意**不掛** `meta.hosting`：後端這支不在代管能力閘後面（種類是主機層的登錄，
      //   與「範圖內有沒有可代管產品」無關）。
      { path: 'agent-kinds', name: 'agent-kinds', component: AgentKindsView, meta: { roles: ADMIN } },
      // 代理商公司管理（批93-C）：建公司、圈產品範圖、停權。superadmin 專屬（後端同）。
      { path: 'resellers', name: 'resellers', component: ResellersView, meta: { roles: MANAGER } },
      // 批141（裁示 26）：合作平台（多租戶平台）管理。**營運主管以上**——後端連讀取都是
      // `require_manager`（平台＝一把能替旗下每個組織取證的鑰匙）。
      { path: 'platforms', name: 'platforms', component: PlatformsView, meta: { roles: MANAGER } },
      {
        path: 'admin-users',
        name: 'admin-users',
        component: AdminUsersView,
        meta: { roles: MANAGER },
      },
      // 批147：超級管理者的 API 金鑰（權限＝擁有者 ∩ 勾選的範圍）。**只有超級管理者**——
      // 後端管理端點 `require_superadmin` 且只認網頁登入（金鑰不能管理金鑰）。
      { path: 'api-keys', name: 'api-keys', component: ApiKeysView, meta: { roles: SUPERADMIN } },
      // 稽核紀錄（批80 批C）：與帳號管理同級＝superadmin 專屬（後端 gate 也是）。
      { path: 'audit', name: 'audit', component: AuditView, meta: { roles: MANAGER } },

      // --- 代理商 realm（批93-C）：只有 `reseller` 進得來，我方員工一律擋在門外 ---
      // 我方員工不放進來不是保密，是避免兩套視角互相污染：這三頁背後打的是
      // `/api/admin/reseller/*`，那個 sub-router 對 staff 一律 403，進來只會整頁紅。
      {
        path: 'reseller/requests',
        name: 'reseller-requests',
        component: ResellerRequestsView,
        meta: { roles: RESELLER },
      },
      {
        path: 'reseller/customers',
        name: 'reseller-customers',
        component: ResellerCustomersView,
        meta: { roles: RESELLER },
      },
      {
        path: 'reseller/licenses',
        name: 'reseller-licenses',
        component: ResellerLicensesView,
        meta: { roles: RESELLER },
      },
      // 兌換碼（批104）：元件**與我方那兩條共用**（差別只在 `lib/redeemApi.ts` 依角色
      // 打哪個前綴）。★ 刻意**不掛** `meta.hosting`——兌換碼屬業務線，與代管能力閘無關。
      {
        path: 'reseller/redeem-campaigns',
        name: 'reseller-redeem-campaigns',
        component: RedeemCampaignsView,
        meta: { roles: RESELLER },
      },
      {
        path: 'reseller/redeem-campaigns/:id',
        name: 'reseller-redeem-campaign-detail',
        component: RedeemCampaignDetailView,
        meta: { roles: RESELLER },
      },
      // 兌換報表（批107 項5）：同樣共用元件。代理商版強制只看自家、**沒有代理商排行**
      // （後端就不算，不是前端隱藏）。與兌換碼一樣**不掛** `meta.hosting`。
      {
        path: 'reseller/redeem-reports',
        name: 'reseller-redeem-reports',
        component: RedeemReportsView,
        meta: { roles: RESELLER },
      },

      // --- 代理商的代管中台（批99-B）---
      //
      // ★★ 元件**與我方員工那六條共用**（`component:` 指的是同一支）——差別只在
      //   `meta.roles` 與 path 前綴，以及 `api/hosting.ts::base()` 會依角色打
      //   `/api/admin/reseller/...`。不複製 view 的理由見 `lib/hostingRoutes.ts`。
      // ★ 為什麼不共用同一條路由、把 `roles` 寫成 `[...STAFF, 'reseller']`：那會在
      //   「代理商只進得了 reseller-* 的頁」這條結構規則上開例外（後端 sub-router
      //   隔離在前端的鏡像），`router/index.spec.ts` 就守不住了。
      // ★ 六條全掛 `meta.hosting`：代理商的範圖不含任何支援代管的產品時，後端整個
      //   `admin_reseller_hosting.router` 有 `require_hosting_scope` → 進來只會整頁 403。
      {
        path: 'reseller/hosts',
        name: 'reseller-hosts',
        component: HostsView,
        meta: { roles: RESELLER, hosting: true },
      },
      {
        path: 'reseller/hosts/:id',
        name: 'reseller-host-detail',
        component: HostDetailView,
        meta: { roles: RESELLER, hosting: true },
      },
      {
        path: 'reseller/jobs',
        name: 'reseller-jobs',
        component: JobsView,
        meta: { roles: RESELLER, hosting: true },
      },
      {
        path: 'reseller/jobs/:jobId',
        name: 'reseller-job-detail',
        component: JobDetailView,
        meta: { roles: RESELLER, hosting: true },
      },
      {
        path: 'reseller/alerts',
        name: 'reseller-alerts',
        component: AlertsView,
        meta: { roles: RESELLER, hosting: true },
      },
      // 開站精靈：代理商也是 admin 級的動作（後端 `POST /reseller/hosts/{id}/sites`
      // 在 `require_reseller` 之外還有開站三重閘＋`activation_policy` 閘）；不放 nav，
      // 一律從主機詳情進去。
      {
        path: 'reseller/provision',
        name: 'reseller-provision',
        component: ProvisionView,
        meta: { roles: RESELLER, hosting: true },
      },
    ],
  },
]

export const router = createRouter({
  // 跟著 vite 的 base 走（批80 批E 起是 '/'）——別再寫死路徑。
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

/**
 * 落地頁：代理商＝我的授權申請；admin 以上＝授權申請；operator（與角色未知）＝代管主機。
 *
 * ★ 回傳的路由**必須**是該角色進得去的（`meta.roles` 含它）——否則 guard 會把人彈到
 *   一個自己也過不了的頁面。`router/index.spec.ts` 對四個角色逐一驗這件事。
 *
 * 批99-A：`hostingVisible` 為 false 時 operator（與角色未知）改落在「部署」——
 * 他原本的落地頁 `hosts` 已經掛了 `meta.hosting`，不換的話一登入就被 guard 彈走。
 * 第二參數**有預設值**是刻意的：既有呼叫端（與 spec 的 `landing(role)`）行為不變。
 */
export function landing(role: string | null | undefined, hostingVisible = true) {
  if (role === 'reseller') return { name: 'reseller-requests' }
  // ★ 批110：用集合比對而不是逐個字串——漏了 `manager` 的話營運主管一登入會被丟到
  //   代管頁（他的主戰場是授權申請），而且不會有任何錯誤訊息。
  if (ADMIN.includes(role as RoleName)) return { name: 'requests' }
  return { name: hostingVisible ? 'hosts' : 'deployments' }
}

/** 這個角色進不進得了這條路由。無 `meta.roles`＝退回「我方員工才可進」。 */
export function canEnter(role: string | null | undefined, roles: RoleName[] | undefined): boolean {
  if (!role) return false
  if (!roles) return (STAFF_ROLES as readonly string[]).includes(role)
  return (roles as string[]).includes(role)
}

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (to.name !== 'login' && !auth.isAuthenticated) return { name: 'login' }
  // 角色只在這裡懶載一次（唯一時機）——落地頁與 meta.roles 都要靠它。
  if (auth.isAuthenticated && auth.role === null) await auth.loadMe()
  if (to.name === 'login' && auth.isAuthenticated) return landing(auth.role)
  // ★ `/login` 到此為止——未登入者不得再往下走 gate（portal 的 guard 一直有這一行，admin 漏了）。
  //   少了它：未登入時 role=null → canEnter 恆 false → landing(null)=hosts → hosts 要登入 →
  //   退回 login → 又跑一次 gate ⇒ **無限重導**。下面那個 `fallback.name === to.name` 的防呆擋不住，
  //   因為 to 是 login、fallback 是 hosts，兩者不同名。
  //   ⚠️ 本機開發看不出來：vue-router 的無限重導保護是 **dev-only**（production build 沒有那道
  //   保護，分頁會真的凍結崩潰）。2026-09-09 ls-dev 1.0.29/1.0.30 實際炸過——未登入者完全進不了後台。
  if (to.name === 'login') return undefined

  // 預設拒絕：沒宣告 `meta.roles` 的路由退回「我方員工」，代理商與角色未知者一律擋下。
  if (!canEnter(auth.role, to.meta.roles)) {
    const fallback = landing(auth.role, auth.hostingVisible)
    // 角色載入失敗（role null）時落地頁自己也過不了 gate → 不再導向，避免無限迴圈
    // （伺服器端 fail-closed，該擋的 API 照樣 403）。
    return fallback.name === to.name ? undefined : fallback
  }
  // 代管能力閘（批99-A）：角色過了還要問「我們有沒有開放代管給你經手的產品」。
  // ★ 一定要在 `canEnter` **之後**——先擋角色再擋能力，訊息語意才不會倒過來
  //   （代理商打 `/hosts` 該說的是「這不是你的頁」而不是「你沒有代管產品」）。
  // ★ 落地頁一律帶 `hostingVisible=false` 算：這條分支只有在 false 時才走得到，
  //   而 operator 的預設落地頁正好是被擋的 `hosts`（不換就變成自己彈自己）。
  if (to.meta.hosting && !auth.hostingVisible) {
    const fallback = landing(auth.role, false)
    return fallback.name === to.name ? undefined : fallback
  }
  return undefined
})
